import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

const SESSION_KEY = 'raajdhani_session';
const ADMIN_TOKEN_KEY = 'raajdhani_admin_token';
const CUSTOMER_TOKEN_KEY = 'raajdhani_customer_token';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [adminToken, setAdminToken] = useState(null);
    const [customerToken, setCustomerToken] = useState(null);
    const [loading, setLoading] = useState(true); // Crucial for stopping the redirect loop

    // Shared Sign Out
    const signOut = () => {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(CUSTOMER_TOKEN_KEY);
        localStorage.removeItem(SESSION_KEY);
        setAdminToken(null);
        setCustomerToken(null);
        setUser(null);
    };

    // Verify session on startup
    useEffect(() => {
        const initAuth = async () => {
            const aToken = localStorage.getItem(ADMIN_TOKEN_KEY);
            const cToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);

            if (aToken || cToken) {
                try {
                    // Ask backend if this token is still valid
                    const res = await api.get('/auth/profile');
                    setUser(res.data.user);
                    setAdminToken(aToken);
                    setCustomerToken(cToken);
                } catch (err) {
                    console.error("Session expired");
                    signOut();
                }
            }
            setLoading(false); // Verification finished
        };
        initAuth();
    }, []);

    // Api.js's response interceptor removes tokens from localStorage and
    // fires a global "auth-logout" event whenever ANY request comes back 401
    // (expired/invalid token). This listener makes the app actually log the
    // user out and redirect the moment a token goes bad.
    useEffect(() => {
        const handleForcedLogout = () => {
            signOut();
            toast.error('Your session has expired. Please log in again.');
        };
        window.addEventListener('auth-logout', handleForcedLogout);
        return () => window.removeEventListener('auth-logout', handleForcedLogout);
    }, []);

    // Admin Login
    const login = (token, adminData) => {
        const userData = { ...adminData, role: 'admin' };
        localStorage.setItem(ADMIN_TOKEN_KEY, token);
        localStorage.setItem(SESSION_KEY, JSON.stringify(userData));
        setAdminToken(token);
        setUser(userData);
    };

    // Customer Login
    const customerLogin = (token, customerData) => {
        const userData = { ...customerData, role: 'customer' };
        localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
        localStorage.setItem(SESSION_KEY, JSON.stringify(userData));
        setCustomerToken(token);
        setUser(userData);
    };

    // FIX: Profile.jsx calls updateUser(...) after successfully saving
    // profile changes, but this function never existed on the context —
    // every profile save threw "updateUser is not a function" and crashed.
    // Merges the given fields into the current user and keeps localStorage
    // in sync so a refresh doesn't lose the update.
    const updateUser = (updates) => {
        setUser(prev => {
            const merged = { ...prev, ...updates };
            localStorage.setItem(SESSION_KEY, JSON.stringify(merged));
            return merged;
        });
    };

    const isAdmin = user?.role === 'admin';

    return (
        <AuthContext.Provider value={{
            user,
            adminToken,
            customerToken,
            isAdmin,
            login,
            customerLogin,
            signOut,
            updateUser,
            loading
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);